import { defineStore, otto } from '@otto-ec/global-resources/nexus';
import type { NexusSignalDefinition } from '@otto-ec/global-resources/nexus';
import type { ResolveInput, SelectionResult, SelectionState } from '@otto-ec/nexus-registry/selection';
import { log } from '@shared/log.browser.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import type { SelectionUpdate } from '@tilelist/application/ports/SelectionEvents.ts';
import type { ViewportSize } from '@tilelist/application/ports/ViewportSize.ts';
import { createEventsOutgoing } from '@tilelist/services/events/EventsOutgoing.browser.ts';
import { publishLegacyResolution } from './LegacySelectionPublisher.browser.ts';
import type {
    ResolvedSelectionResponse,
    SelectionDomAdapter,
    SelectionResolver,
    SelectionTrackingAdapter,
    TrackingIdentity,
} from './SelectionDependencies.ts';
import { defaultSelectionNavigation } from './SelectionNavigation.browser.ts';
import type { ForwardSelectionSource, SelectionNavigation } from './SelectionNavigation.browser.ts';
import { handleSelectionNavigation } from './SelectionNavigationHandler.browser.ts';
import { orderedPage, pageFromAttributes, sameSelectionExceptUrl, selectionParams } from './SelectionState.ts';

export type SelectionDependencies = {
    resolver: SelectionResolver;
    dom: SelectionDomAdapter;
    tracking: SelectionTrackingAdapter;
    viewport: ViewportSize;
    eventsOutgoing?: EventsOutgoing;
    trackUrl?: (url: string) => void;
    navigation?: SelectionNavigation;
};

export type ResolveOptions = {
    /*                                                                                                                    */
    commit?: boolean;
};

export type ResolvedSelection = SelectionResult & {
    /*                                                       */
    pageMergeId: string;
};

const logger = log.scope('selection');

export class Selection {
    private latestOperation = 0;
    private latestPublication = 0;
    private current: NexusSignalDefinition<SelectionState> | undefined;
    private navigation: SelectionNavigation | undefined;
    private readonly updateHandlers = new Set<(update: SelectionUpdate) => void | Promise<void>>();
    private readonly eventsOutgoing: EventsOutgoing;
    /*                                               */
    private pageMergeId = '';

    public constructor(private readonly dependencies: SelectionDependencies) {
        this.eventsOutgoing = dependencies.eventsOutgoing ?? createEventsOutgoing();
    }

    public register(): () => void {
        if (this.current) throw new Error('Selection is already registered');
        logger.debug('Registering selection');

        try {
            this.navigation = this.dependencies.navigation ?? defaultSelectionNavigation();
            const identity = this.dependencies.dom.currentIdentity();
            this.pageMergeId = identity.pageMergeId;
            const state = this.navigation.initialSelection(identity.rule);
            this.current = defineStore<SelectionState>(state);
            const unregisterNamespace = otto.selection.assignNamespace({
                resolve: this.resolve,
                current: this.current,
            });
            const unsubscribeNavigation = this.navigation.subscribe(this.handleNavigation);
            void this.navigateCheckpoint(state, 'full', 'replace', true).catch(() => undefined);
            return () => {
                unsubscribeNavigation();
                unregisterNamespace();
            };
        } catch (error) {
            logger.error('Failed to register selection', error);
            this.current = undefined;
            this.navigation = undefined;
            throw error;
        }
    }

    public readonly resolve = async (input: ResolveInput, options: ResolveOptions = {}): Promise<ResolvedSelection> => {
        logger.debug('Resolving selection', input, options);
        const result = await this.resolveAttributes(input.attributes);
        if (options.commit === false) return result;
        const committed = result.count !== 0;
        if (committed) await this.navigateCheckpoint(result.selection, 'full', 'push', false, result.pageMergeId);
        this.publishLegacy(result, committed ? result.pageMergeId : this.pageMergeId);
        return result;
    };

    public onUpdate(handler: (update: SelectionUpdate) => void | Promise<void>): () => void {
        this.updateHandlers.add(handler);
        return () => this.updateHandlers.delete(handler);
    }

    public currentSelection(): SelectionState {
        if (!this.current) throw new Error('Selection is not registered');
        return otto.selection.current.get();
    }

    public async navigate(selection: Omit<SelectionState, 'entryRule'>, source: ForwardSelectionSource): Promise<void> {
        const current = this.currentSelection();
        const next = { ...selection, entryRule: current.entryRule };
        let pageMergeId: string | undefined;
        if (!sameSelectionExceptUrl(next, current)) {
            const renewal = await this.renewTrackingIdentity(next);
            if (renewal === SUPERSEDED) return;
            pageMergeId = renewal;
        }
        await this.navigateCheckpoint(next, source, 'push', false, pageMergeId);
    }

    /*                                                  */
    private async resolveAttributes(attributes: Record<string, string | string[]>): Promise<ResolvedSelection> {
        const response = await this.fetchResolution(selectionParams(attributes));
        const entryRule = otto.selection.current.get().entryRule;
        if (!entryRule) throw new Error('Failed to resolve rule: missing entry rule');

        return {
            selection: { rule: response.rule, page: pageFromAttributes(attributes), url: response.url, entryRule },
            ...(response.count !== undefined ? { count: response.count } : {}),
            pageMergeId: response.pageMergeId,
        };
    }

    private readonly handleNavigation = (
        event: Parameters<Parameters<SelectionNavigation['subscribe']>[0]>[0],
        checkpoint: unknown,
    ): void => {
        handleSelectionNavigation(
            event,
            checkpoint,
            (update, destinationUrl, pageMergeId) => void this.publishNavigation(update, destinationUrl, pageMergeId),
            otto.selection.current.get(),
        );
    };

    /*                                                                                         */
    private async publishNavigation(
        update: SelectionUpdate,
        destinationUrl: string,
        pageMergeId: string | undefined,
    ): Promise<void> {
        const publication = ++this.latestPublication;
        if (update.source === 'historic') {
            const renewal = await this.renewTrackingIdentity(update.selection);
            if (renewal === SUPERSEDED) return;
            pageMergeId = renewal;
        }
        if (pageMergeId)
            await this.applyTrackingIdentity({ pageMergeId, rule: update.selection.rule }, update.selection.url);
        /*                                                                    */
        if (publication !== this.latestPublication) return;

        this.dependencies.trackUrl?.(destinationUrl);
        otto.selection.current.emit(update.selection, { retain: true });
        for (const handler of this.updateHandlers) void handler(update);

        const result = { selection: { ...update.selection, page: orderedPage(update.selection.page) } };
        /*                                                                                   */
        if (update.source !== 'full') this.publishLegacy(result, this.pageMergeId);
    }

    /*                                                                                 */
    /*                                                                           */
    private async renewTrackingIdentity(selection: SelectionState): Promise<string | undefined | typeof SUPERSEDED> {
        const params = selectionParams({
            rule: selection.rule,
            ...(selection.page.sortOrder !== undefined ? { sortiertnach: selection.page.sortOrder } : {}),
        });
        try {
            return (await this.fetchResolution(params)).pageMergeId;
        } catch (error) {
            if (error instanceof SupersededError) return SUPERSEDED;
            logger.error('Failed to renew tracking identity', error);
            return undefined;
        }
    }

    private async navigateCheckpoint(
        selection: SelectionState,
        source: ForwardSelectionSource,
        history: 'push' | 'replace' = 'push',
        initialize = false,
        pageMergeId?: string,
    ): Promise<void> {
        if (!this.navigation) throw new Error('Selection is not registered');
        try {
            await this.navigation.navigate(selection, source, history, initialize, pageMergeId);
        } catch (error) {
            logger.error('Failed to navigate selection', error);
            throw error;
        }
    }

    private async fetchResolution(params: URLSearchParams): Promise<ResolvedSelectionResponse> {
        const sequence = ++this.latestOperation;
        const response = await this.dependencies.resolver(params, sequence);
        if (sequence !== this.latestOperation) throw new SupersededError();
        return response;
    }

    private async applyTrackingIdentity(identity: TrackingIdentity, trackingUrl: string): Promise<void> {
        try {
            this.dependencies.dom.applyIdentity(identity);
            this.pageMergeId = identity.pageMergeId;
            await this.dependencies.tracking.replaceContext(trackingUrl);
        } catch (error) {
            logger.error('Failed to apply tracking identity', error);
        }
    }

    private publishLegacy(result: SelectionResult, ssid: string): void {
        try {
            publishLegacyResolution(this.eventsOutgoing, this.dependencies.viewport, result, ssid);
        } catch (error) {
            logger.error('Failed to publish legacy selection resolution', error);
        }
    }
}

const SUPERSEDED = Symbol('superseded');

class SupersededError extends Error {
    public constructor() {
        super('Selection operation was superseded by a newer operation');
    }
}
